<script lang="ts" setup>
import { computed, getCurrentInstance, ref } from 'vue';
import { finalizeConfirmRequestApi } from '@Api/requestFlow.api';
import { useFieldStatus, useFormValidation, type ValidationRules } from '@Shared';
import { REJECTION_REASONS } from '@Constants/confirmRejectionReasons.constants';
import type { ConfirmRejectionReason } from '@Constants/confirmRejectionReasons.constants';
import ConfirmModal from '@Components/modals/shared/ConfirmModal.vue';
import type { ConfirmFooterButton } from '@Components/modals/shared/ConfirmModal.vue';

const props = defineProps<{
    modelValue: boolean;
}>();

const emit = defineEmits<{
    (e: 'update:modelValue', value: boolean): void;
    (e: 'rejected', data: { reason: string; additionalDetails: string | null }): void;
}>();

const instance = getCurrentInstance();
const proxy = instance?.proxy as { $apptoast: (msg: string, type: string) => void } | undefined;

const isSubmitting = ref(false);
const selectedRejectionReason = ref<ConfirmRejectionReason | null>(null);
const rejectionAdditionalDetails = ref<string | null>(null);

const rejectionFormData = computed(() => ({
    reason: selectedRejectionReason.value
}));

const validationRules = computed<ValidationRules>(() => ({
    reason: [{ required: true, message: 'Reason is required' }]
}));

const formValidation = useFormValidation(rejectionFormData, validationRules);
const { turnOnValidation, hasError } = formValidation;
const { getFieldStatus, getFieldMessage } = useFieldStatus(formValidation);

const isModalOpen = computed({
    get: () => props.modelValue,
    set: (value: boolean) => emit('update:modelValue', value)
});

const closeModal = () => {
    isModalOpen.value = false;
};

const submitRejection = async () => {
    isSubmitting.value = true;
    try {
        await turnOnValidation(true);
        if (hasError.value ||
            selectedRejectionReason.value?.value === null ||
            selectedRejectionReason.value?.value === undefined) return;

        await finalizeConfirmRequestApi(
            false,
            selectedRejectionReason.value.value,
            rejectionAdditionalDetails.value
        );
        emit('rejected', {
            reason: selectedRejectionReason.value.text,
            additionalDetails: rejectionAdditionalDetails.value,
        });
    } catch (error) {
        console.error('submitRejection error', error);
        proxy?.$apptoast('Something went wrong. Please try again.', 'error');
    } finally {
        isSubmitting.value = false;
    }
};

const footerButtons = computed<ConfirmFooterButton[]>(() => [
    {
        type: 'outline',
        text: 'Cancel',
        dataId: 'confirm-reject-no',
        isDisabled: isSubmitting.value,
        onClick: closeModal
    },
    {
        type: 'primary',
        text: 'Submit',
        dataId: 'confirm-reject-yes',
        isLoading: isSubmitting.value,
        onClick: submitRejection
    }
]);
</script>

<template>
    <confirm-modal
        v-model="isModalOpen"
        icon="cd-ic-paper-x"
        icon-style="red"
        header="Why are you rejecting?"
        sub-header="Please select a reason and briefly explain your rejection."
        :footer-buttons="footerButtons"
    >
        <template #form-elements>
            <div class="confirm-reject-modal__form-elements">
                <app-dropdown
                    v-model="selectedRejectionReason"
                    is-required
                    data-id="rejection-reason"
                    :options="REJECTION_REASONS"
                    label="Reason"
                    size="large"
                    id-field="value"
                    display-field="text"
                    :field-status="getFieldStatus('reason')"
                    :message="getFieldMessage('reason')"
                    data-validation-field="reason" 
                />

                <app-text-area-input
                    v-model="rejectionAdditionalDetails"
                    data-id="rejection-additional-details"
                    placeholder="Enter your message here."
                    label="Additional Details" 
                />
            </div>
        </template>
    </confirm-modal>
</template>

<style lang="scss" scoped>
@use 'certifid-v2-client-hub-styles' as *;

.confirm-reject-modal {
    &__form-elements {
        display: flex;
        flex-direction: column;
        gap: 1.5rem;
        margin-top: 1.5rem;
        text-align: left;
    }
}
</style>